import { useState } from "react";
import { Button, Input, Label, Modal, Switch } from "@fox1ck-org/ui";
import { toast } from "@fox1ck-org/ui/toast";
import type { ReportView, ReportViewDefinition } from "@gen/tracker/v1/reporting_pb";

import { useT } from "@/lib/i18n";
import { useCreateReportView } from "@/lib/services/report-view-service";
import { toastFailure } from "@/lib/plan";

/**
 * «Сохранить вид» (board CampSaveView): a name, whether everyone in the
 * workspace sees it, and what it keeps — filters, folding, columns, sort.
 * The period is not kept: it is the Top bar's, the same on every page.
 */
export function SaveViewDialog({
  open,
  onClose,
  definition,
  summary,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  definition: ReportViewDefinition;
  summary: { filters: string; fold: string; columns: string; sort: string };
  onSaved: (view: ReportView) => void;
}) {
  const t = useT();
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const create = useCreateReportView();
  const summaryRows: [string, string][] = [
    [t("camps.view.keeps.filters"), summary.filters],
    [t("camps.view.keeps.fold"), summary.fold],
    [t("camps.view.keeps.columns"), summary.columns],
    [t("camps.view.keeps.sort"), summary.sort],
  ];
  const save = () =>
    create.mutate(
      { name: name.trim(), definition, workspaceShared: shared },
      {
        onSuccess: (view) => {
          toast.success(t("camps.view.saved", { name: view?.name ?? name.trim() }));
          if (view) onSaved(view);
          setName("");
          setShared(false);
          onClose();
        },
        onError: (e) => toastFailure(e, t("rep.saveFailed")),
      },
    );
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("camps.view.title")}
      description={t("camps.view.description")}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("camps.cancel")}
          </Button>
          <Button onClick={save} disabled={!name.trim() || create.isPending}>
            {t("camps.view.save")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="camps-view-name" textCase="none">
            {t("camps.view.name")}
          </Label>
          <Input
            id="camps-view-name"
            font="sans"
            autoFocus
            value={name}
            placeholder={t("camps.view.namePlaceholder")}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && name.trim() && save()}
          />
        </div>
        <label className="flex items-center gap-2.5 text-sm">
          <Switch checked={shared} onCheckedChange={setShared} />
          {t("camps.view.shared")}
          <span className="ml-auto text-xs text-muted-foreground">{t("camps.view.sharedHint")}</span>
        </label>
        {/* The summary as the board draws it — and as «Сохранить отчёт» does: the words, then the value beside them. */}
        <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1.5 rounded-md bg-surface-2 px-3 py-2.5 text-sm">
          {summaryRows.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="truncate">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Modal>
  );
}
